import { defineStore } from "pinia";
import { usePiniaStore } from "@/stores/store";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { getOrganisationColour } from "@/methods/organisationColour";
import {
    ActiveOrganisationSettings,
    OrganisationProfile,
    SchoolSettings,
    UserPermissions,
} from "@/types/school";

/** Stores only a non-authoritative selection preference, never permissions or settings. */
const preferredOrganisationKey = (email: string) =>
    `organisation:last-selected:${email}`;
/** Outlives logout so the user lands on their chosen organisation at next sign-in. */
const favouriteOrganisationKey = (email: string) =>
    `organisation:favourite:${email}`;

export const useOrganisationStore = defineStore("organisation", {
    state: () => ({
        availableOrganisations: [] as OrganisationProfile[], // List of organisations the user has access to
        activeOrganisationShortcode: null as string | null, // The shortcode of the currently active organisation
        activeOrganisationSettings: null as SchoolSettings | null, // The settings of the currently active organisation
        /** Forces organisation-scoped route components to remount in place. */
        contextVersion: 0,
        /** Keeps the current page covered while its new organisation context loads. */
        isSwitching: false,
        favouriteOrganisationShortcode: null as string | null,
    }),
    getters: {
        getAvailableOrganisations: (state) => state.availableOrganisations,
        getActiveOrganisationShortcode: (state) =>
            state.activeOrganisationShortcode,
        getActiveOrganisation: (state): OrganisationProfile | null =>
            state.availableOrganisations.find(
                ({ shortcode }) =>
                    shortcode === state.activeOrganisationShortcode,
            ) ?? null,
        getActiveOrganisationColour: (state): string | null =>
            state.activeOrganisationShortcode
                ? getOrganisationColour(
                      state.activeOrganisationShortcode,
                      state.availableOrganisations.map(
                          ({ shortcode }) => shortcode,
                      ),
                  )
                : null,
        getSchoolShortcodes(): string[] {
            return this.getActiveOrganisation
                ? [this.getActiveOrganisation.shortcode]
                : [];
        },
        getPermissions(): UserPermissions | null {
            return this.getActiveOrganisation?.permissions ?? null;
        },
        /**
         * Gets the combined school settings for the active organisation.
         * @returns The merged school settings, or null if no active organisation exists.
         */
        getSchoolSettings(): ActiveOrganisationSettings | null {
            const profile = this.getActiveOrganisation;
            if (!profile) return null;
            return { ...this.activeOrganisationSettings, ...profile };
        },
    },
    actions: {
        setAvailableOrganisations(organisations: OrganisationProfile[]) {
            this.availableOrganisations = organisations;
        },
        setActiveOrganisationSettings(settings: SchoolSettings) {
            // A slow response can arrive after the user has switched organisation.
            if (settings.shortcode !== this.activeOrganisationShortcode) return;
            this.activeOrganisationSettings = settings;
        },
        getPreferredOrganisation(email: string | null) {
            if (!email) return null;

            return localStorage.getItem(preferredOrganisationKey(email));
        },
        setPreferredOrganisation(email: string | null, shortcode: string) {
            if (!email) return;

            localStorage.setItem(preferredOrganisationKey(email), shortcode);
        },
        loadFavouriteOrganisation(email: string | null) {
            this.favouriteOrganisationShortcode = email
                ? localStorage.getItem(favouriteOrganisationKey(email))
                : null;
            return this.favouriteOrganisationShortcode;
        },
        toggleFavouriteOrganisation(email: string | null, shortcode: string) {
            if (!email) return;

            if (this.favouriteOrganisationShortcode === shortcode) {
                localStorage.removeItem(favouriteOrganisationKey(email));
                this.favouriteOrganisationShortcode = null;
                return;
            }
            localStorage.setItem(favouriteOrganisationKey(email), shortcode);
            this.favouriteOrganisationShortcode = shortcode;
        },
        /**
         * Selects the organisation with the given shortcode as the active organisation.
         * @param shortcode The shortcode of the organisation to select.
         * @returns True if the organisation was successfully selected, false otherwise.
         */
        selectOrganisation(shortcode: string) {
            if (shortcode === this.activeOrganisationShortcode) return true;

            const selectedOrganisation = this.availableOrganisations.find(
                (organisation) => organisation.shortcode === shortcode,
            );
            if (!selectedOrganisation) return false;

            usePiniaStore().clearOrganisationData();
            this.activeOrganisationShortcode = shortcode;
            this.activeOrganisationSettings = null;
            this.contextVersion += 1;
            this.isSwitching = true;
            return true;
        },
        /**
         * Clears the organisation context, including available organisations, active organisation, and settings.
         * @param email The email of the user for whom to clear the preferred organisation. If null, the preferred
         * organisation is not cleared.
         */
        clearOrganisationContext(email: string | null = null) {
            usePiniaStore().clearOrganisationData();
            this.contextVersion += 1;
            useOnboardingStore().reset();
            this.availableOrganisations = [];
            this.activeOrganisationShortcode = null;
            this.activeOrganisationSettings = null;
            this.isSwitching = false;
            this.favouriteOrganisationShortcode = null;
            // Remove obsolete persisted context as well as the active session context.
            usePiniaStore().clearLegacyOrganisationContext();

            if (email) {
                localStorage.removeItem(preferredOrganisationKey(email));
            }
        },
        /**
         * Marks the end of an organisation switch, updating the switching state.
         * This should be called after the organisation switch process is complete.
         */
        finishSwitching() {
            this.isSwitching = false;
        },
    },
    persist: {
        pick: [
            "availableOrganisations",
            "activeOrganisationShortcode",
            "activeOrganisationSettings",
        ],
        storage: sessionStorage,
        /**
         * Called after the store has been hydrated from the persisted state.
         * Can be used to perform any necessary adjustments or checks on the store data.
         */
        afterHydrate: ({ store }) => {
            // Sessions saved before the flat profile shape must reload from the API.
            if (
                store.availableOrganisations.some(
                    (organisation: { shortcode?: unknown }) =>
                        typeof organisation.shortcode !== "string",
                )
            ) {
                store.clearOrganisationContext();
            }
        },
    },
});
